import { useState, useCallback, ReactNode } from "react";
import { Box, Flex, Text, TextArea, TextField, Button, Separator } from "@radix-ui/themes";
import { Maximize2, Check, X } from "lucide-react";
import Modal from "@/components/ui/Modal";
import { RichTextEditor } from "./RichTextEditor";

interface ExpandableTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  /** "plain" = textarea, "rich" = TipTap rich text editor */
  mode?: "plain" | "rich";
  /** Show variable toggle button */
  variableToggle?: boolean;
  /** Description text below the field */
  description?: string;
  required?: boolean;
  variablePicker?: ReactNode;
}

/**
 * A text field that can expand to a near-fullscreen modal for editing longer content.
 * Supports both plain text (textarea) and rich text (TipTap) modes.
 * Used in workflow step editors for prompts, post content, system prompts, etc.
 */
export default function ExpandableTextField({
  value,
  onChange,
  label,
  placeholder,
  rows = 4,
  mode = "plain",
  variableToggle = false,
  description,
  required,
  variablePicker,
}: ExpandableTextFieldProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [isVariable, setIsVariable] = useState(() =>
    typeof value === "string" && value.includes("{{")
  );

  const handleExpand = useCallback(() => {
    setTempValue(value);
    setIsFullScreen(true);
  }, [value]);

  const handleSave = useCallback(() => {
    onChange(tempValue);
    setIsFullScreen(false);
  }, [tempValue, onChange]);

  const handleCancel = useCallback(() => {
    setIsFullScreen(false);
  }, []);

  return (
    <Box>
      {/* Header with label, variable toggle, and expand button */}
      <Flex justify="between" align="center" mb="1">
        {label && (
          <Text as="label" size="2" weight="medium">
            {label}
            {required && <Text color="red"> *</Text>}
          </Text>
        )}
        <Flex gap="2" align="center">
          {variablePicker}
          {variableToggle && (
            <Button
              type="button"
              variant="ghost"
              size="1"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsVariable(!isVariable); }}
              style={{ fontSize: "11px", padding: "2px 6px" }}
            >
              {isVariable ? "Use editor" : "Use variable"}
            </Button>
          )}
          {!isVariable && (
            <>
              {variableToggle && <Separator orientation="vertical" size="1" />}
              <Button
                type="button"
                variant="ghost"
                size="1"
                onClick={handleExpand}
                style={{ fontSize: "11px", padding: "2px 6px", gap: "4px" }}
              >
                <Maximize2 size={12} />
                Expand
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      {description && (
        <Text size="1" color="gray" style={{ display: "block", marginBottom: 6 }}>
          {description}
        </Text>
      )}

      {/* Inline editor — resizable via CSS resize */}
      {isVariable ? (
        <TextField.Root
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "{{ $anythink.steps.step_key.data.field }}"}
          style={{ fontFamily: "monospace", fontSize: "13px" }}
        />
      ) : mode === "rich" ? (
        <Box style={{ border: "1px solid var(--gray-6)", borderRadius: "var(--radius-2)", overflow: "hidden" }}>
          <RichTextEditor
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
          />
        </Box>
      ) : (
        <TextArea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          style={{ resize: "vertical", minHeight: `${rows * 24}px` }}
        />
      )}

      {/* Fullscreen modal */}
      <Modal
        isOpen={isFullScreen}
        onClose={handleCancel}
        title={label || "Edit Content"}
        size="full"
        actions={
          <Flex gap="2">
            <Button size="2" variant="soft" color="red" onClick={handleCancel}>
              <X size={16} style={{ marginRight: "4px" }} />
              Cancel
            </Button>
            <Button size="2" variant="soft" color="green" onClick={handleSave}>
              <Check size={16} style={{ marginRight: "4px" }} />
              Save
            </Button>
          </Flex>
        }
      >
        <Box style={{ padding: "16px" }}>
          {mode === "rich" ? (
            <RichTextEditor
              value={tempValue}
              onChange={setTempValue}
              placeholder={placeholder}
              rows={20}
            />
          ) : (
            <TextArea
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              placeholder={placeholder}
              style={{ width: "100%", fontSize: "14px", resize: "vertical", minHeight: "200px" }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
